import React, { useEffect, useState } from "react";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const formatDateTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString();
};

export default function GroupClassStudentsTab({ tutorId, classes, onReloadClasses }) {
  const [selectedClassId, setSelectedClassId] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!classes?.length) {
      setSelectedClassId("");
      setData(null);
      return;
    }

    const hasCurrentSelection = classes.some((item) => Number(item.id) === Number(selectedClassId));
    if (!hasCurrentSelection) {
      setSelectedClassId(String(classes[0].id));
    }
  }, [classes, selectedClassId]);

  useEffect(() => {
    if (!selectedClassId || !tutorId) {
      return;
    }

    const fetchEnrollments = async () => {
      setLoading(true);
      setError("");

      try {
        const res = await axios.get(
          `${BASE_URL}/api/tutors/group-classes/${selectedClassId}/enrollments/${tutorId}`
        );
        setData(res.data?.data || null);
      } catch (err) {
        console.error(err);
        setError(err?.response?.data?.message || "Failed to load enrolled students");
      } finally {
        setLoading(false);
      }
    };

    fetchEnrollments();
  }, [selectedClassId, tutorId]);

  return (
    <section className="space-y-5">
      <div className="flex flex-col md:flex-row gap-3 md:items-end md:justify-between">
        <div>
          <h2 className="text-2xl font-black">Manage Students Per Class</h2>
          <p className="text-slate-500 text-sm mt-1">Choose a class to see who booked a seat.</p>
        </div>

        <button
          type="button"
          onClick={onReloadClasses}
          className="self-start md:self-auto rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
        >
          Refresh Classes
        </button>
      </div>

      {classes.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-5 text-slate-600">
          You have not created any group class yet.
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <label className="block text-sm font-semibold text-slate-700 mb-2">Select Class</label>
            <select
              value={selectedClassId}
              onChange={(e) => setSelectedClassId(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              {classes.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.title} ({new Date(item.start_date).toLocaleDateString()})
                </option>
              ))}
            </select>
          </div>

          {error && (
            <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="rounded-xl border border-slate-200 bg-white p-5 text-slate-600">
              Loading enrolled students...
            </div>
          ) : data ? (
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="mb-4">
                <h3 className="text-lg font-bold text-slate-900">{data.class?.title}</h3>
                <p className="text-sm text-slate-500">Start: {formatDateTime(data.class?.start_date)}</p>
                <p className="text-sm text-slate-500">Enrolled students: {data.enrolled_count}</p>
              </div>

              {data.enrollments?.length ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left border-b border-slate-200 text-slate-600">
                        <th className="py-2 pr-3">Student</th>
                        <th className="py-2 pr-3">Email</th>
                        <th className="py-2 pr-3">Phone</th>
                        <th className="py-2 pr-3">Payment</th>
                        <th className="py-2 pr-3">Booked At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.enrollments.map((row) => (
                        <tr key={row.id} className="border-b border-slate-100 text-slate-700">
                          <td className="py-3 pr-3 font-semibold">{row.student_name}</td>
                          <td className="py-3 pr-3">{row.student_email || "-"}</td>
                          <td className="py-3 pr-3">{row.student_phone || "-"}</td>
                          <td className="py-3 pr-3 uppercase">{row.payment_status}</td>
                          <td className="py-3 pr-3">{formatDateTime(row.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-slate-600">No student has booked this class yet.</p>
              )}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
